/**
 * RefChip — chip parametrizado por `RefState` para usar dentro de
 * `ContextSectionsBlock` (Doc 24 §3 Fase 8 + ADR-0002 Fase 10.f.1).
 *
 * Estados:
 *   - `intra`: chip estándar con título de la sección + X delete.
 *   - `cross_canon`: documento fuente OFICIAL — sin warning.
 *   - `cross_draft`: documento fuente está en borrador, el contenido
 *     puede cambiar tras aprobarse → warning ámbar.
 *   - `cross_missing`: el documento fuente aún no se creó en el proyecto
 *     que use este template → warning rojo (la IA generará sin contexto).
 *   - `cross_empty`: el documento fuente existe pero la sección
 *     referenciada está vacía → warning ámbar.
 *
 * En el designer del template no hay proyecto, así que los estados
 * `canon` / `draft` / `missing` / `empty` no son resolubles. El caller
 * (`ContextSectionsBlock`) pasa `cross_canon` como estado neutro para
 * declaraciones cross-template — el render muestra el template + sección
 * referenciada de forma legible. Cuando el chip se reuse en el editor
 * del artifact dentro de un proyecto, el caller pasará el estado real.
 */

import { AlertCircle, FileQuestion, FileText, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { SupportedLocale } from '../../../i18n/config'

export type RefState =
  | 'intra'
  | 'cross_canon'
  | 'cross_draft'
  | 'cross_missing'
  | 'cross_empty'

export interface RefChipProps {
  /** Ref tal cual aparece en `context_sections[]`: `"problem_context"` o
   * `"tpl-x.problem_context"`. Nombre `refValue` (no `ref`) para no chocar
   * con el atributo reservado de React. */
  refValue: string
  state: RefState
  /** Título bilingüe para intra refs.  */
  title?: { es: string; en: string }
  /** Para cross refs: título del documento fuente (template) y de la
   * sección consumida — se muestra como `template_name → section_title`. */
  crossSource?: {
    templateName: { es: string; en: string }
    sectionTitle: { es: string; en: string }
  }
  /** Locale activo del designer — se usa para elegir es/en del título. */
  locale: SupportedLocale
  /** Modo readonly oculta el botón de borrar. */
  readOnly?: boolean
  onRemove(): void
  /** ID estable para tests. */
  testId?: string
}

interface CrossStyle {
  border: string
  bg: string
  iconColor: string
  badgeBg: string
  badgeText: string
  Icon: typeof FileText
  i18nLabelKey: string
  i18nTooltipKey: string
}

const CROSS_STYLES: Record<
  Exclude<RefState, 'intra'>,
  CrossStyle
> = {
  cross_canon: {
    border: 'border-official/40',
    bg: 'bg-official/5',
    iconColor: 'text-official',
    badgeBg: 'bg-official/15',
    badgeText: 'text-official',
    Icon: FileText,
    i18nLabelKey: 'designer.editor.context_sections.chip_cross_canon_label',
    i18nTooltipKey: 'designer.editor.context_sections.chip_cross_canon_tooltip',
  },
  cross_draft: {
    border: 'border-amber-500/40',
    bg: 'bg-amber-500/5',
    iconColor: 'text-amber-600',
    badgeBg: 'bg-amber-500/15',
    badgeText: 'text-amber-700',
    Icon: FileText,
    i18nLabelKey: 'designer.editor.context_sections.chip_cross_draft_label',
    i18nTooltipKey: 'designer.editor.context_sections.chip_cross_draft_tooltip',
  },
  cross_missing: {
    border: 'border-critical/40',
    bg: 'bg-critical/5',
    iconColor: 'text-critical',
    badgeBg: 'bg-critical/15',
    badgeText: 'text-critical',
    Icon: FileQuestion,
    i18nLabelKey: 'designer.editor.context_sections.chip_cross_missing_label',
    i18nTooltipKey:
      'designer.editor.context_sections.chip_cross_missing_tooltip',
  },
  cross_empty: {
    border: 'border-amber-500/40',
    bg: 'bg-amber-500/5',
    iconColor: 'text-amber-600',
    badgeBg: 'bg-amber-500/15',
    badgeText: 'text-amber-700',
    Icon: AlertCircle,
    i18nLabelKey: 'designer.editor.context_sections.chip_cross_empty_label',
    i18nTooltipKey: 'designer.editor.context_sections.chip_cross_empty_tooltip',
  },
}

export function RefChip({
  refValue,
  state,
  title,
  crossSource,
  locale,
  readOnly = false,
  onRemove,
  testId,
}: RefChipProps) {
  const { t } = useTranslation('templates')

  if (state === 'intra') {
    const label = title?.[locale] ?? title?.['es'] ?? refValue
    return (
      <li
        data-testid={testId}
        data-state={state}
        title={refValue}
        className="inline-flex items-center gap-1 rounded-md border border-brand/30 bg-brand/5 px-2 py-0.5 text-[11px] text-ink"
      >
        <span className="truncate max-w-[200px]">{label}</span>
        {!readOnly ? (
          <button
            type="button"
            onClick={onRemove}
            className="ml-0.5 text-ink-subtle hover:text-critical"
            aria-label={t('designer.editor.context_sections_remove') as string}
            data-testid={testId ? `${testId}-remove` : undefined}
          >
            <X className="w-3 h-3" />
          </button>
        ) : null}
      </li>
    )
  }

  const style = CROSS_STYLES[state]
  const { Icon } = style
  // Para cross refs, el label legible compone "Template → Sección" si
  // tenemos `crossSource`; si no, mostramos el refValue crudo como
  // fallback (mejor que silencio).
  const tplLabel =
    crossSource?.templateName[locale] ??
    crossSource?.templateName['es'] ??
    refValue.split('.')[0]
  const sectionLabel =
    crossSource?.sectionTitle[locale] ??
    crossSource?.sectionTitle['es'] ??
    refValue.split('.').slice(1).join('.')

  // Tooltip explica el estado + ref técnico al pasar el cursor.
  const tooltipText = `${t(style.i18nTooltipKey) as string} — ${refValue}`

  return (
    <li
      data-testid={testId}
      data-state={state}
      title={tooltipText}
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] text-ink ${style.border} ${style.bg}`}
    >
      <Icon className={`w-3 h-3 shrink-0 ${style.iconColor}`} />
      <span className="truncate max-w-[260px]">
        <span className="font-medium">{tplLabel}</span>
        <span className="text-ink-subtle"> · </span>
        <span>{sectionLabel}</span>
      </span>
      <span
        className={`rounded px-1 text-[9px] uppercase tracking-wider ${style.badgeBg} ${style.badgeText}`}
        data-testid={testId ? `${testId}-cross-badge` : undefined}
      >
        {t(style.i18nLabelKey)}
      </span>
      {!readOnly ? (
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 text-ink-subtle hover:text-critical"
          aria-label={t('designer.editor.context_sections_remove') as string}
          data-testid={testId ? `${testId}-remove` : undefined}
        >
          <X className="w-3 h-3" />
        </button>
      ) : null}
    </li>
  )
}
